import { FC } from 'react';

import Box from '@mui/material/Box';
import Popper from '@mui/material/Popper';
import cn from 'classnames';

import OButton from '../OButton';
import { IPopper } from './OPopper.types';
import { useOPopper } from './useOPopper';
import OPopperGroups from './OPopperGroups';
import styles from './OPopper.module.scss';

const OPopper: FC<IPopper> = ({
  children,
  groups,
  icon,
  isOpen = false,
  isActive,
  customStyles,
  classes,
  position = 'top-end',
  onOpen,
  onClose,
  bgColor,
  offset,
  anchorEl,
  withArrow = false,
  isDismissible = true,
  disableClose = false,
  closeOnScroll = true,
  tooltipContent = '',
  isToolTipSmall = false,
  toolTipPlacement = 'top',
  triggerId,
  popperId,
}) => {
  const {
    open,
    active,
    modifiers,
    popperRef,
    anchorRef,
    submenuAnchor,
    submenuActions,
    subMenuPopperRef,
    toggleClick,
    handleClose,
    arrowRefCallback,
    handleSubmenuClick,
    handleSubmenuToggle,
  } = useOPopper({
    isOpen,
    offset,
    isActive,
    withArrow,
    isDismissible,
    onOpen,
    onClose,
    closeOnScroll,
  });

  return (
    <>
      {/* Trigger button */}
      <OButton
        ref={anchorRef}
        onClick={toggleClick}
        onPointerDown={(e) => {
          // Prevent focus change when clicking the trigger
          // This keeps text selection in textarea intact
          e.preventDefault();
        }}
        id={triggerId}
        icon={icon}
        isToolTipSmall={isToolTipSmall}
        toolTipContent={tooltipContent}
        toolTipPlacement={toolTipPlacement}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={popperId ? `${popperId}-menu` : undefined}
        size="default"
        variant="none"
        className={cn(classes?.containerStyle, classes?.iconButton, {
          'popper-icon__active': active,
        })}
      />

      {/* Main Popper */}
      <Popper
        open={open}
        ref={popperRef}
        id={popperId}
        anchorEl={anchorEl ?? anchorRef.current}
        placement={position}
        modifiers={modifiers}
        className={styles.popperRoot}
        style={{
          width: customStyles?.width ?? 'auto',
          ...customStyles?.root,
        }}
      >
        <Box
          role="menu"
          id={popperId ? `${popperId}-menu` : undefined}
          className={cn(styles.menuBox, styles.boxRoot)}
          style={{
            backgroundColor: bgColor ?? 'var(--public-chat-bg)',
            ...customStyles?.boxRoot,
          }}
          onClick={!disableClose ? handleClose : undefined}
        >
          {/* Render groups if provided, else children */}
          {groups ? (
            <OPopperGroups groups={groups} customStyles={customStyles} handleSubmenuToggle={handleSubmenuToggle} />
          ) : (
            children
          )}
        </Box>

        {withArrow && <span ref={arrowRefCallback} className="popper-arrow" />}
      </Popper>

      {/* Submenu Popper */}
      <Popper
        open={!!submenuAnchor && !!submenuActions}
        anchorEl={submenuAnchor}
        ref={subMenuPopperRef}
        placement="right-start"
        className={styles.submenuPopper}
      >
        <Box id={`${popperId}-submenu`} className={styles.submenuBox}>
          {submenuActions?.map((sub, si) => (
            <Box
              id={`${popperId}-submenu-item-${si}`}
              key={si}
              role="menuitem"
              className={styles.submenuItem}
              onClick={() => handleSubmenuClick(sub)}
            >
              {sub.icon}
              {sub.label}
            </Box>
          ))}
        </Box>
      </Popper>
    </>
  );
};

export default OPopper;
